import { styled } from '@linaria/react'
import React, {
  type ComponentProps,
  type FC,
  type PropsWithChildren,
} from 'react'
import { type FontSize, type FontWeight, Typography } from './Typography'
import { RouterLink } from './RouterLink'
import { Loader } from './Loader'
import { colors, type ColorValue } from '../styles/colors'

export const ButtonLink: FC<
  PropsWithChildren & {
    to: string
    className?: string
    textColor?: ColorValue
    textWeight?: FontWeight
  }
> = ({
  to,
  children,
  className,
  textColor = colors.secondary,
  textWeight = 600,
}) => {
  return (
    <RouterLink to={to} style={{ display: 'block' }} className={className}>
      <Typography fontSize={16} fontWeight={textWeight} color={textColor}>
        {children}
      </Typography>
    </RouterLink>
  )
}

export const ButtonRegular: FC<
  ComponentProps<typeof ButtonStyled> & {
    loading?: boolean
    className?: string
    textColor?: ColorValue
    backgroundColor?: ColorValue
    textWeight?: FontWeight
    fontSize?: FontSize
    type?: 'button' | 'submit' | 'reset'
    isWithTypographyWrapper?: boolean
    noShadow?: boolean
  }
> = ({
  children,
  loading,
  disabled,
  className,
  textColor = colors.secondary,
  textWeight = 600,
  type = 'button',
  fontSize = 16,
  isWithTypographyWrapper = true,
  noShadow = false,
  ...props
}) => {
  if (isWithTypographyWrapper) {
    return (
      <ButtonStyled
        disabled={disabled || loading}
        type={type}
        className={className}
        noShadow={noShadow}
        {...props}
      >
        <Typography
          fontSize={fontSize}
          fontWeight={textWeight}
          color={textColor}
        >
          <ButtonContent>
            <ButtonText $hidden={loading}>{children}</ButtonText>
            {loading && <ButtonLoader />}
          </ButtonContent>
        </Typography>
      </ButtonStyled>
    )
  }
  return (
    <ButtonStyled
      disabled={disabled || loading}
      type={type}
      className={className}
      noShadow={noShadow}
      {...props}
    >
      <ButtonContent>
        <ButtonText $hidden={loading}>{children}</ButtonText>
        {loading && <ButtonLoader />}
      </ButtonContent>
    </ButtonStyled>
  )
}

const ButtonContent = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const ButtonText = styled.span<{ $hidden?: boolean }>`
  visibility: ${props => (props.$hidden ? 'hidden' : 'visible')};
`

const ButtonLoader = styled(Loader)`
  position: absolute;
  inset: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`
const ButtonStyled = styled.button<{
  backgroundColor?: ColorValue
  noShadow?: boolean
}>`
  transition:
    opacity 0.2s,
    transform 0.1s ease,
    box-shadow 0.1s ease;
  padding: 16px 20px;
  border-radius: ${colors.radiusMd};
  cursor: pointer;
  text-align: center;
  background-color: ${props => props.backgroundColor ?? 'transparent'};
  box-shadow: ${props => (props.noShadow ? 'none' : colors.shadowSm)};

  &:disabled {
    opacity: 0.5;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
    box-shadow: ${props => (props.noShadow ? 'none' : colors.shadowMd)};
  }

  & > * {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`
